import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, TrendingUp, Target, Award } from 'lucide-react';
import { companyDescription, assets } from '../utils/mock';
import { Button } from './ui/button';

export const About = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const handleLearnMore = () => {
    navigate(companyDescription.ctaLink);
  };

  return (
    <section
      id="about-section"
      ref={sectionRef}
      className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
    >
      {/* Background Accent */}
      <div className="absolute top-0 left-0 w-full h-2 bg-[#29367A]"></div>

      <div className="w-full max-w-7xl mx-auto px-8">
        <div className="grid md:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Text Content */}
          <div
            className={`transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {companyDescription.description}
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 gap-4 mb-8">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#29367A]/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#29367A]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Value-Driven</h4>
                  <p className="text-sm text-gray-600">
                    Disciplined approach to investing
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#29367A]/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#29367A]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Long-Term Focus</h4>
                  <p className="text-sm text-gray-600">
                    Consistent market outperformance
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#29367A]/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-[#29367A]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Concentrated Portfolio
                  </h4>
                  <p className="text-sm text-gray-600">
                    Select Indian equities, different weightages
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleLearnMore}
              className="group bg-[#29367A] hover:bg-[#1f2859] text-white px-8 py-6 text-base font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {companyDescription.ctaText}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* Image */}
          <div
            className={`transition-all duration-1000 delay-200 transform ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={assets.aboutImage}
                  alt="Value Investing"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#29367A]/40 to-transparent"></div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#29367A]/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#29367A]/10 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
